import React from 'react';
import { StaffButton } from './ui';

// Portal-wide render-crash boundary. Both portal layouts (StaffLayout,
// TeachLayout) wrap their <Outlet/> in this, and App.js wraps each portal's
// top-level <Suspense> in it too (a failed LAYOUT chunk surfaces there, above
// the in-layout boundary). A crash renders a branded recovery card instead of
// white-screening the whole portal.
//
// Deliberately quiet: no stack traces on screen — the error still reaches the
// console via componentDidCatch. `resetKey` (the layouts pass
// location.pathname) clears the crashed state on navigation.
//
// Stale-deploy chunks (2026-09-28, review L2): after a deploy, a tab opened on
// the previous build asks for chunk files that no longer exist. That is not a
// bug in the page — a reload fixes it — so a chunk-load failure reloads the
// page ONCE automatically (guarded in sessionStorage so a genuinely broken
// network can't loop), then falls back to a "new version" card.

const CHUNK_RELOAD_KEY = 'portal_chunk_reload_at';
const CHUNK_RELOAD_WINDOW_MS = 2 * 60 * 1000;

export function isChunkLoadError(error) {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  const msg = String(error.message || error);
  return /Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(msg);
}

function readReloadAt() {
  try {
    return Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
  } catch {
    return 0;
  }
}

// Returns true when it triggered the one automatic reload.
export function reloadOnceForChunkError(now = Date.now()) {
  if (now - readReloadAt() < CHUNK_RELOAD_WINDOW_MS) return false;
  try {
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
  } catch {
    // No storage → no loop guard → don't auto-reload; the card still offers it.
    return false;
  }
  window.location.reload();
  return true;
}

function NoteIcon() {
  // Same understated eighth-note brand moment as the kit EmptyState.
  return (
    <svg viewBox="0 0 24 24" className="w-7 h-7 mx-auto mb-3 text-primary/25" aria-hidden="true" fill="none">
      <path
        d="M10.5 17.2V4.9c3 .9 4.7 2.7 4.7 5.2 0 1.1-.3 2.1-.9 3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <ellipse cx="8.1" cy="17.4" rx="2.6" ry="2" fill="currentColor" />
    </svg>
  );
}

export default class PortalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { crashed: false, chunk: false };
  }

  static getDerivedStateFromError(error) {
    return { crashed: true, chunk: isChunkLoadError(error) };
  }

  componentDidCatch(error, info) {
    console.error('Portal render crash:', error, (info && info.componentStack) || '');
    if (isChunkLoadError(error)) reloadOnceForChunkError();
  }

  componentDidUpdate(prevProps) {
    if (this.state.crashed && prevProps.resetKey !== this.props.resetKey) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ crashed: false, chunk: false });
    }
  }

  render() {
    if (!this.state.crashed) return this.props.children;
    const { chunk } = this.state;
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 text-center" role="alert">
        <NoteIcon />
        <h1 className="font-headline text-2xl text-on-surface m-0 mb-2">
          {chunk ? 'The portal was just updated' : 'Something broke'}
        </h1>
        <p className="text-sm text-on-surface-variant mt-0 mb-6">
          {chunk
            ? 'This tab is still running the previous version. Reload to get the latest — nothing you entered elsewhere is lost.'
            : 'This page hit an unexpected error. Reloading usually clears it — nothing you entered elsewhere is lost.'}
        </p>
        <StaffButton onClick={() => window.location.reload()}>Reload</StaffButton>
      </div>
    );
  }
}

// Per-SECTION boundary (review L4, folded with Home's per-card boundary in
// Design Wave 3): one broken card on a busy page (a Front Desk section, a
// Home card choking on a malformed row) renders a compact inline notice and
// the rest of the page keeps working. "Try again" re-mounts just this
// section; no page reload needed.
//   label     what broke ("Rooms today")
//   scope     what still works ("Home" → "The rest of Home still works.")
//   compact   one-line notice sized for a Home card (default: section card)
export class SectionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { crashed: false, attempt: 0 };
    this.retry = this.retry.bind(this);
  }

  static getDerivedStateFromError() {
    return { crashed: true };
  }

  componentDidCatch(error, info) {
    console.error(`Section render crash (${this.props.label || 'section'}):`, error, (info && info.componentStack) || '');
  }

  retry() {
    this.setState((s) => ({ crashed: false, attempt: s.attempt + 1 }));
  }

  render() {
    if (!this.state.crashed) {
      return <React.Fragment key={this.state.attempt}>{this.props.children}</React.Fragment>;
    }
    const { compact = false, scope } = this.props;
    const label = this.props.label || (compact ? 'This card' : 'This section');
    const rest = scope ? `The rest of ${scope} still works.` : 'The rest of this page still works.';
    return (
      <div
        role="alert"
        className={`rounded-card border border-outline-variant bg-surface-container-low flex flex-wrap items-center justify-between ${
          compact ? 'px-4 py-2 gap-x-3' : 'px-4 py-4 sm:px-5 gap-3'
        } ${this.props.className || (compact ? 'mb-6' : 'mt-6')}`}
      >
        <p className="m-0 text-sm text-on-surface">
          <span className="font-label">{label} couldn&apos;t load.</span>{' '}
          <span className="text-on-surface-variant">{rest}</span>
        </p>
        {compact ? (
          <button
            type="button"
            onClick={this.retry}
            className="min-h-11 px-2 font-label text-sm text-primary hover:underline underline-offset-2"
          >
            Try again
          </button>
        ) : (
          <StaffButton variant="ghost" onClick={this.retry}>Try again</StaffButton>
        )}
      </div>
    );
  }
}
